import { useState } from 'react';
import { verifyPin } from '../services/api';

interface PinModalProps {
  onSuccess: () => void;
  /** Omit to make the prompt non-dismissable (dashboard gate). */
  onCancel?: () => void;
  title?: string;
  description?: string;
}

// The session now lives in an httpOnly cookie issued by the server after a correct PIN.
// Remove the old client-side flag so it cannot be mistaken for access.
const LEGACY_SESSION_KEY = 'settings_auth';
export function clearLegacySession(): void {
  try {
    localStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // storage unavailable - nothing to clean
  }
}

export function PinModal({
  onSuccess,
  onCancel,
  title = 'Enter Settings PIN',
  description = 'Enter your PIN to access Settings',
}: PinModalProps) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setVerifying(true);

    try {
      const result = await verifyPin(pin);
      if (result.valid) {
        onSuccess();
      } else if (result.retryAfterSec) {
        setError(`Too many attempts. Try again in ${Math.ceil(result.retryAfterSec / 60)} min.`);
        setPin('');
      } else if (result.error && result.error !== 'Invalid PIN') {
        setError(result.error);
        setPin('');
      } else {
        setError('Invalid PIN');
        setPin('');
      }
    } catch {
      setError('Failed to verify PIN');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="pin-modal-overlay" onClick={onCancel}>
      <div className="pin-modal" onClick={e => e.stopPropagation()}>
        <h3>{title}</h3>
        <p className="pin-description">{description}</p>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            value={pin}
            onChange={e => setPin(e.target.value)}
            placeholder="Enter PIN"
            autoFocus
            maxLength={20}
            className="pin-input"
            aria-label="PIN"
          />
          {error && <div className="pin-error">{error}</div>}
          <div className="pin-buttons">
            {onCancel && (
              <button type="button" onClick={onCancel} className="pin-cancel">
                Cancel
              </button>
            )}
            <button type="submit" disabled={!pin || verifying} className="pin-submit">
              {verifying ? 'Verifying...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
